import { computed, inject, Injectable, signal } from '@angular/core';
import {
  collection,
  Firestore,
  getDocs,
  query,
  where,
} from '@angular/fire/firestore';
import { NGXLogger } from 'ngx-logger';
import { getFunctions, httpsCallable } from '@angular/fire/functions';
import { environment } from '../../environments/environment';
import { Product } from '../models/product.model';

export type AudienceType = 'church' | 'school' | 'user';

@Injectable({ providedIn: 'root' })
export class ProductService {
  private logger = inject(NGXLogger);
  private firestore = inject(Firestore);
  private productsCollection = collection(this.firestore, 'products');

  private _loading = signal<boolean>(true);
  private _allProducts = signal<Product[]>([]);

  readonly loading = this._loading.asReadonly();
  readonly allProducts = computed(() => this._allProducts());
  readonly schoolProducts = computed(() =>
    this._allProducts().filter(
      (p) => !p.audiences || p.audiences.length === 0 || p.audiences.includes('school')
    )
  );

  readonly churchProducts = computed(() =>
    this._allProducts().filter(
      (p) => !p.audiences || p.audiences.length === 0 || p.audiences.includes('church')
    )
  );

  constructor() {
    this.loadProducts();
  }

  private async loadProducts() {
    this._loading.set(true);
    this.logger.debug('Loading products from Firestore...');

    try {
      const q = query(
        this.productsCollection,
        where('audiences', 'array-contains-any', [
          'church',
          'school',
          'user',
        ] as const),
        where('livemode', '==', environment.production)
      );

      let snapshot = await getDocs(q);
      let products = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Product)
      );

      // Auto-populate: If empty or in development mode, call pullStripeProducts cloud function to synchronize products
      if (products.length === 0 || !environment.production) {
        this.logger.debug('Auto-populating or forcing development sync from Stripe...');
        const functions = getFunctions();
        const pullFn = httpsCallable(functions, 'pullStripeProducts');
        try {
          await pullFn();
          this.logger.debug('Stripe sync successful.');
          // Query again
          snapshot = await getDocs(q);
          products = snapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as Product)
          );
        } catch (syncErr) {
          console.error('Stripe sync failed:', syncErr);
        }
      }

      this._allProducts.set(products);
      this.logger.debug(`Loaded ${products.length} products from Firestore.`);
      this.logger.debug(JSON.stringify(this._allProducts()));
    } catch (err) {
      console.error(err);
      this._allProducts.set([]);
    } finally {
      this._loading.set(false);
      this.logger.debug('Finished loading products from Firestore.');
    }
  }

  public refresh() {
    this.loadProducts();
  }
}
